import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { classifyContainmentVec2PolyLine2Fast, computeConvexVertexInteriorPoint } from "geom/containment";
import { polyLine2PolyLine2DoIntersect } from "geom/intersect";
import { poleOfInaccessibilityPolyLine2 } from "geom/label";
import { polyline2PointCloudCentroid } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { GeneratedFloorBoundaries } from "./floorAssemblyTypes";
import type { FloorBay, SplitLine } from "./floorSplitDataTypes";

/**
 * Mirrors a Vec2 interior point by reflecting its x-coordinate and translating.
 * The y-coordinate is preserved.
 */
function mirrorInteriorPoint(point: Vec2, mirrorTranslationX: number): Vec2 {
  return [-point[0] + mirrorTranslationX, point[1]];
}

/**
 * Computes a stable interior point for a bay polygon using pole-of-inaccessibility.
 * Falls back to centroid, then to a convex-vertex triangle centroid for highly
 * concave or degenerate polygons.
 */
export function computeBayInteriorPoint(polygon: PolyLine2): Vec2 {
  const pole = poleOfInaccessibilityPolyLine2(polygon);
  if (pole) {
    return pole;
  }
  const centroid = polyline2PointCloudCentroid(polygon);
  if (centroid && classifyContainmentVec2PolyLine2Fast(centroid[0], centroid[1], polygon)) {
    return centroid;
  }
  return computeConvexVertexInteriorPoint(polygon);
}

/**
 * Matches generated bay polygons to existing bay definitions by testing whether
 * each bay's stored `interiorPoint` falls inside a generated polygon.
 * Bays are assumed non-overlapping so each interiorPoint falls in at most one polygon.
 *
 * When `mirror` is true, stored interiorPoints are mirrored before containment testing
 * because the generated polygons are already in mirrored coordinates.
 *
 * Unmatched polygons get new UIDs. Existing bays preserve their stored `interiorPoint`;
 * new bays get a freshly computed one.
 */
export function matchBaysByInteriorPoint(
  bayPolygons: PolyLine2[],
  existingBays: Table<FloorBay>,
  mirror: boolean,
  mirrorTranslationX: number,
): { bayIds: string[]; updatedBays: Table<FloorBay> } {
  if (bayPolygons.length === 0) {
    return { bayIds: [], updatedBays: {} };
  }

  const result: string[] = new Array(bayPolygons.length).fill("");
  const matched = new Set<string>();
  const updatedBays: Record<string, FloorBay> = {};

  for (const [bayId, bay] of Object.entries(existingBays)) {
    const pt = mirror ? mirrorInteriorPoint(bay.interiorPoint, mirrorTranslationX) : bay.interiorPoint;
    for (let i = 0; i < bayPolygons.length; i++) {
      if (result[i] !== "") {
        continue;
      }
      if (classifyContainmentVec2PolyLine2Fast(pt[0], pt[1], bayPolygons[i])) {
        result[i] = bayId;
        matched.add(bayId);
        break;
      }
    }
  }

  for (let i = 0; i < bayPolygons.length; i++) {
    if (result[i] === "") {
      result[i] = uid();
    }
    const existing = existingBays[result[i]];
    const interiorPoint = existing?.interiorPoint ?? computeBayInteriorPoint(bayPolygons[i]);
    updatedBays[result[i]] = { ...existing, id: result[i], interiorPoint };
  }

  return { bayIds: result, updatedBays };
}

/**
 * Removes bays whose interiorPoint does not fall inside any of the given polygons.
 * Used after a split line is deleted to prune bays that no longer correspond to any
 * generated sub-polygon, preventing unbounded growth of the bays table.
 */
export function removeOrphanBays(bays: Table<FloorBay>, polygons: PolyLine2[]): Table<FloorBay> {
  if (polygons.length === 0) {
    return {};
  }
  const result: Record<string, FloorBay> = {};
  for (const [bayId, bay] of Object.entries(bays)) {
    const pt = bay.interiorPoint;
    for (const polygon of polygons) {
      if (classifyContainmentVec2PolyLine2Fast(pt[0], pt[1], polygon)) {
        result[bayId] = bay;
        break;
      }
    }
  }
  return result;
}

/**
 * Returns the subset of split lines that intersect a closed perimeter.
 * A split line is considered intersecting if any of its vertices or segment
 * midpoints lie strictly inside the perimeter, or if any of its segments
 * cross a perimeter edge.
 */
export function splitLinesIntersectingPerimeter(perimeter: PolyLine2, splitLines: SplitLine[]): SplitLine[] {
  return splitLines.filter((sl) => {
    const pl = sl.polyline;
    if (!pl || pl.length < 4) {
      return false;
    }
    for (let i = 0; i < pl.length - 2; i += 2) {
      if (classifyContainmentVec2PolyLine2Fast(pl[i], pl[i + 1], perimeter)) {
        return true;
      }
      const mx = (pl[i] + pl[i + 2]) / 2;
      const my = (pl[i + 1] + pl[i + 3]) / 2;
      if (classifyContainmentVec2PolyLine2Fast(mx, my, perimeter)) {
        return true;
      }
    }
    const lastIdx = pl.length - 2;
    if (classifyContainmentVec2PolyLine2Fast(pl[lastIdx], pl[lastIdx + 1], perimeter)) {
      return true;
    }
    return splitLineSegmentsCrossPerimeter(pl, perimeter);
  });
}

function splitLineSegmentsCrossPerimeter(splitPl: PolyLine2, perimeter: PolyLine2): boolean {
  return polyLine2PolyLine2DoIntersect(splitPl, perimeter);
}

/**
 * Builds a FloorBay for a virtual (unsplit) bay that has no entry in splitData.bays.
 * Computes interiorPoint from generated floor boundaries so the bay can be
 * persisted to the doc without missing its required interiorPoint field.
 */
export function resolveVirtualBay(
  floorBoundaries: Table<GeneratedFloorBoundaries>,
  floorId: string,
  bayId: string,
): FloorBay {
  const bayBoundaries = getBayBoundariesForFloor(floorBoundaries, floorId);
  const match = bayBoundaries.find((b) => b.bayId === bayId);
  const perimeter = match?.boundary.perimeters[0];
  const interiorPoint: Vec2 = perimeter ? computeBayInteriorPoint(perimeter) : [0, 0];
  return { id: bayId, interiorPoint };
}

export interface PartitionedParentFloor {
  readonly id: string;
  readonly boundary: GeneratedFloorBoundaries;
}

export interface PartitionedBayBoundary {
  readonly bayId: string;
  readonly boundary: GeneratedFloorBoundaries;
}

export interface PartitionedFloorBoundaries {
  readonly parentFloors: readonly PartitionedParentFloor[];
  readonly baysByFloorId: ReadonlyMap<string, readonly PartitionedBayBoundary[]>;
}

/**
 * Partitions a flat `Table<GeneratedFloorBoundaries>` into parent floor entries
 * and bay entries grouped by their parent floor ID. Bay entries are identified
 * by having both `parentFloorId` and `bayId` set; all other entries are parent floors.
 *
 * This avoids repeated O(N) scans when callers need to look up bays for a given floor.
 */
export function partitionFloorBoundaries(floorBoundaries: Table<GeneratedFloorBoundaries>): PartitionedFloorBoundaries {
  const parentFloors: PartitionedParentFloor[] = [];
  const baysByFloorId = new Map<string, PartitionedBayBoundary[]>();

  for (const key in floorBoundaries) {
    const entry = floorBoundaries[key];
    if (entry.bayId && entry.parentFloorId) {
      let arr = baysByFloorId.get(entry.parentFloorId);
      if (!arr) {
        arr = [];
        baysByFloorId.set(entry.parentFloorId, arr);
      }
      arr.push({ bayId: entry.bayId, boundary: entry });
    } else {
      parentFloors.push({ id: key, boundary: entry });
    }
  }

  return { parentFloors, baysByFloorId };
}

/**
 * Returns the bay boundaries for a given floor ID from a pre-partitioned result,
 * or partitions on-the-fly from a raw table. Convenience for call sites that
 * only need bays for a single floor.
 */
export function getBayBoundariesForFloor(
  floorBoundaries: Table<GeneratedFloorBoundaries>,
  floorId: string,
): readonly PartitionedBayBoundary[] {
  return partitionFloorBoundaries(floorBoundaries).baysByFloorId.get(floorId) ?? [];
}
